import { useState, useContext, useLayoutEffect, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { useSites, useCurrentSite } from '@hotjar/site';
import { Text, tokens, Loader } from '@hotjar/hotsauce';
import { ErrorBoundary } from '@hotjar/error-handler';

import { IntegrationSiteDropdown } from './components/IntegrationSiteDropdown';
import { IntegrationCard } from './components/IntegrationCard';
import { LegacyContext } from './context/global-context';
import { useIntegrations } from './hooks/use-integrations';
import { integrationGatesOpen } from './utils';
import type { Integration } from './types';

const Page = styled.article`
  padding: 0 ${tokens.spacer.x2} ${tokens.spacer.x4};
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled(Text)`
  margin: ${tokens.spacer.x6} 0 0;
`;

const SectionTitle = styled(Text)`
  margin-bottom: ${tokens.spacer.x1pt5};
  margin-top: ${tokens.spacer.x4};
`;

const SelectBox = styled.div`
  width: 320px;
  margin-bottom: ${tokens.spacer.x1};
`;

const IntegrationsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  grid-gap: ${tokens.spacer.x3};
`;

export const IntegrationsPage = (): JSX.Element | null => {
  const { usePageTitle = () => null } = useContext(LegacyContext);
  const { data: currentSite } = useCurrentSite();
  const {
    query: { data: sites = [] },
  } = useSites();
  const [selectedSite, setSelectedSite] = useState(currentSite);
  const { t } = useTranslation();
  const { integrations, externalIntegrations, refreshTokens, isLoading } = useIntegrations(
    selectedSite?.id,
  );
  const [activeIntegrations, setActiveIntegrations] = useState<Integration[]>([]);
  const [availableIntegrations, setAvailableIntegrations] = useState<Integration[]>([]);

  usePageTitle(`${t('integrations:page.title')} - Hotjar`);

  useEffect(() => {
    if (selectedSite) {
      refreshTokens();
    }
    // disabling eslint rule as having refreshToken in the deps would create an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const isIntegrationActive = (int: Integration) =>
      integrationGatesOpen(int) && (int.isEnabled || int.isAlwaysOn);

    setActiveIntegrations(integrations.filter((int) => isIntegrationActive(int)));
    setAvailableIntegrations(integrations.filter((int) => !isIntegrationActive(int)));
  }, [integrations, isLoading, selectedSite]);

  useLayoutEffect(() => {
    if (!selectedSite && currentSite) {
      setSelectedSite(currentSite);
    }
  }, [currentSite, selectedSite, setSelectedSite]);

  const handleSiteChange = useCallback(
    (siteId: string) => {
      const newSite = sites.find((site) => site.id === Number(siteId));

      if (newSite) {
        setSelectedSite(newSite);
      }
    },
    [sites],
  );

  if (!selectedSite) {
    return null;
  }

  return (
    <ErrorBoundary squadSentryTag="integrations">
      <Page>
        <PageTitle as="h3" textType="header3">
          {t('integrations:page.title')}
        </PageTitle>
        <SectionTitle as="p" textType="subtitle2">
          {t('integrations:page.dropdown-title')}
        </SectionTitle>
        <SelectBox>
          {sites.length > 0 && (
            <IntegrationSiteDropdown
              value={selectedSite?.id ?? 0}
              onChange={handleSiteChange}
              sites={sites}
            />
          )}
        </SelectBox>
        <Loader isLoading={isLoading}>
          {activeIntegrations.length > 0 ? (
            <>
              <SectionTitle
                as="p"
                textType="subtitle1"
                aria-label={t('integrations:page.active-integrations')}
              >
                {t('integrations:page.active-integrations')}
              </SectionTitle>
              <IntegrationsContainer>
                {activeIntegrations.map((integration) => (
                  <IntegrationCard
                    key={integration.name}
                    integration={integration}
                    enabled
                    siteId={selectedSite.id}
                    {...integration}
                  />
                ))}
              </IntegrationsContainer>
            </>
          ) : null}
          {availableIntegrations.length > 0 ? (
            <>
              <SectionTitle
                as="p"
                textType="subtitle1"
                aria-label={t('integrations:page.available-integrations')}
              >
                {t('integrations:page.available-integrations')}
              </SectionTitle>
              <IntegrationsContainer>
                {availableIntegrations.map((integration) => (
                  <IntegrationCard
                    key={integration.name}
                    integration={integration}
                    enabled={false}
                    siteId={selectedSite.id}
                  />
                ))}
              </IntegrationsContainer>
            </>
          ) : null}
          {externalIntegrations.length > 0 ? (
            <>
              <SectionTitle
                as="p"
                textType="subtitle1"
                aria-label={t('integrations:page.external-integrations')}
              >
                {t('integrations:page.external-integrations')}
              </SectionTitle>
              <IntegrationsContainer>
                {externalIntegrations.map((integration) => (
                  <IntegrationCard
                    key={integration.name}
                    integration={integration}
                    enabled={false}
                    hideButton
                    siteId={selectedSite.id}
                  />
                ))}
              </IntegrationsContainer>
            </>
          ) : null}
        </Loader>
      </Page>
    </ErrorBoundary>
  );
};
